<template>
  <div class="app-container">
    <!-- 상단 메뉴바 (Solum 전용 레이아웃일 때는 숨김) -->
    <header class="top-navbar" v-if="showSidebar">
      <!-- 로고와 메뉴 버튼이 있는 상단 바 -->
      <div class="navbar-header">
        <div class="navbar-logo">
          <img 
            :src="companyLogo" 
            alt="Company Logo" 
            class="top-company-logo"
            :style="$tenant.logo === 'SDAA.png' ? { filter: 'brightness(0) hue-rotate(240deg) saturate(300%) brightness(0.6)' } : {}"
          />
          <!-- 로고에 회사명이 포함된 테넌트(hideCompanyName)는 회사명 생략 -->
          <span v-if="!$tenant.hideCompanyName" class="company-name">{{ $tenant.displayName !== $tenant.id ? $tenant.displayName : displayCompanyName($company) }}</span>
        </div>
        <div class="navbar-actions">
          <!-- 홈 아이콘 -->
          <div class="home-icon" @click="goToHome">
            <span class="material-symbols-outlined">home</span>
          </div>
          <!-- 알림 종 아이콘 (테넌트 features.notifications) -->
          <div v-if="$tenant.features.notifications" class="notification-bell" @click="goToNotifications">
            <span class="material-symbols-outlined bell-icon">notifications</span>
            <span v-if="notificationCount > 0" class="notification-badge">{{ notificationCount }}</span>
          </div>
          <!-- 언어 토글 (KO / EN) -->
          <div class="lang-toggle" role="group" :aria-label="$t('lang.ko') + ' / ' + $t('lang.en')">
            <button
              type="button"
              class="lang-btn"
              :class="{ active: $i18n.locale === 'ko' }"
              @click="switchLang('ko')"
            >KO</button>
            <button
              type="button"
              class="lang-btn"
              :class="{ active: $i18n.locale === 'en' }"
              @click="switchLang('en')"
            >EN</button>
          </div>
          <button @click="toggleSidebar" class="navbar-menu-button">
            <span class="hamburger-icon">☰</span>
            <span class="menu-text">{{ $t('common.menu') }}</span>
          </button>
        </div>
      </div>
      
      <!-- 접었다 펼쳐지는 메뉴 영역 -->
      <nav :class="['dropdown-menu', { 'menu-open': isSidebarOpen }]">
        <div class="menu-grid">
          <div class="menu-section">
            <h4>{{ $t('nav.mainMenu') }}</h4>
            <!-- 병동형 테넌트는 메인 메뉴에서 기본 대시보드 숨김 -->
            <router-link
              v-if="$tenant.layout !== 'sdaa'"
              to="/dashboard"
              @click="closeSidebar"
            >{{ $t('nav.dashboard') }}</router-link>

            <router-link
              v-if="$tenant.layout === 'sdaa'"
              to="/oldTagList"
              @click="closeSidebar"
            >{{ $t('nav.tagList') }}</router-link>
            <router-link
              v-else
              to="/tagList"
              @click="closeSidebar"
            >{{ $t('nav.tagList') }}</router-link>

            <router-link v-if="$company !== 'PNK'" to="/assetMap" @click="closeSidebar">{{ $t('nav.assetMap') }}</router-link>
            <router-link v-if="$company === 'PNK'" to="/assetMap2" @click="closeSidebar">{{ $t('nav.assetMap') }}</router-link>
            <router-link v-if="$company === 'post' || $company === 'POST'" to="/tagRegisterPost" @click="closeSidebar">{{ $t('nav.tagRegisterPost') }}</router-link>
            <router-link v-if="$company === 'post' || $company === 'POST'" to="/tagUpdatePost" @click="closeSidebar">{{ $t('nav.tagUpdatePost') }}</router-link>
            <router-link v-if="$company === 'post' || $company === 'POST'" to="/rtHistory" @click="closeSidebar">{{ $t('nav.rtHistory') }}</router-link>

            <router-link to="/" @click="logout">{{ $t('nav.logout') }}</router-link>
          </div>

          <div class="menu-section" v-if="$isadmin === 1">
            <h4>{{ $t('nav.adminMenu') }}</h4>
            <router-link to="/gatewayList" @click="closeSidebar">{{ $t('nav.gatewayList') }}</router-link>
            <router-link to="/notificationCenter" @click="closeSidebar">{{ $t('nav.notificationCenter') }}</router-link>
            <router-link to="/registerTag" @click="closeSidebar">{{ $t('nav.registerTag') }}</router-link>
            <router-link to="/registerGateway" @click="closeSidebar">{{ $t('nav.registerGateway') }}</router-link>
            <router-link v-if="!isSolum" to="/wsServerUrlList" @click="closeSidebar">{{ $t('nav.wsServerUrlList') }}</router-link>
            <router-link v-if="!isSolum" to="/otaServerUrlList" @click="closeSidebar">{{ $t('nav.otaServerUrlList') }}</router-link>
            <router-link to="/gatewayControl" @click="closeSidebar">{{ $t('nav.gatewayControl') }}</router-link>
            <router-link v-if="!isSolum" to="/assetHitmap" @click="closeSidebar">{{ $t('nav.assetHitmap') }}</router-link>
            <router-link v-if="!isSolum" to="/companyMap" @click="closeSidebar">{{ $t('nav.companyMap') }}</router-link>
            <!-- 병동형(sdaa 레이아웃) 테넌트: Admin 메뉴에 병동 대시보드 표시 -->
            <router-link
              v-if="$tenant.layout === 'sdaa'"
              to="/oldTagList"
              @click="closeSidebar"
            >DashboardSDAA</router-link>
            <router-link v-if="$company !== 'PNK' && $company !== 'Wintest' && !isSolum" to="/logisticMap" @click="closeSidebar">{{ $t('nav.logisticMap') }}</router-link>
          </div>
        </div>
      </nav>
    </header>

    <!-- 메인 컨텐츠 영역 -->
    <div class="content-container">
      <router-view></router-view>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'App',
  data() {
    return {
      isSidebarOpen: false,
      companyLogo: this.getCompanyLogo(),
      notificationCount: 0,
    };
  },
  created() {
    console.log('App created - $company:', this.$company);
    console.log('App created - $isadmin:', this.$isadmin);
    this.fetchNotificationCount();
    // 30초마다 알림 개수 업데이트
    this._notifPollId = setInterval(() => {
      this.fetchNotificationCount();
    }, 30000);
    // NotificationCenter 등에서 acknowledge 발생 시 즉시 갱신
    this._notifChangedHandler = () => this.fetchNotificationCount();
    window.addEventListener('notification:changed', this._notifChangedHandler);
  },
  beforeUnmount() {
    if (this._notifPollId) clearInterval(this._notifPollId);
    if (this._notifChangedHandler) {
      window.removeEventListener('notification:changed', this._notifChangedHandler);
    }
  },
  methods: {
    async fetchNotificationCount() {
      try {
        const company = localStorage.getItem('company');
        if (!company) return;
        const response = await axios.get('/api/getNotification', { params: { company } });
        this.notificationCount = response.data.notification || 0;
      } catch (error) {
        console.error('Failed to fetch notification count:', error);
        this.notificationCount = 0;
      }
    },
    goToNotifications() {
      this.$router.push('/notificationCenter');
    },
    goToHome() {
      // 회사별 홈 페이지 = 테넌트 프리셋 homeRoute
      this.$router.push(this.$tenant.homeRoute || '/dashboard');
    },
    getCompanyLogo() {
      const company = localStorage.getItem('company');
      
      // 테넌트 프리셋에 로고 파일이 지정된 경우
      if (this.$tenant.logo) {
        try {
          return require(`@/assets/${this.$tenant.logo}`);
        } catch (error) {
          console.error('Tenant logo not found:', this.$tenant.logo);
          return require('@/assets/Skaichips_LOGO.png'); // 기본 로고
        }
      }
      
      // 다른 회사들의 로고 처리
      try {
        return require(`@/assets/${this.assetCompanyName(company)}_LOGO.png`);
      } catch (error) {
        console.error(`Logo not found for company: ${company}`);
        return require('@/assets/Skaichips_LOGO.png'); // 기본 로고
      }
    },
    assetCompanyName(company) {
      if (!company) return 'Skaichips';
      return company.toLowerCase() === 'skychips' ? 'Skaichips' : company;
    },
    displayCompanyName(company) {
      return this.assetCompanyName(company);
    },
    toggleSidebar() {
      this.isSidebarOpen = !this.isSidebarOpen;
    },
    // 언어 전환 — vue-i18n locale 변경 + localStorage 동기화
    switchLang(lang) {
      // i18n.js 에서 setLocale export 했지만 여기서는 단순히 $i18n.locale 직접 변경하고
      // localStorage 만 맞춰주는 방식으로 충분 (페이지 새로고침 없이 즉시 반영).
      if (lang !== 'ko' && lang !== 'en') return;
      this.$i18n.locale = lang;
      try { localStorage.setItem('lang', lang); } catch (e) { /* ignore */ }
      if (typeof document !== 'undefined') {
        document.documentElement.setAttribute('lang', lang);
      }
    },
    closeSidebar() {
      this.isSidebarOpen = false;
    },
    logout() {
      localStorage.removeItem('token');
      localStorage.removeItem('company');
      localStorage.removeItem('mustChangePassword');
      // 전역 속성 초기화가 필요할 수 있음
      this.$root.$emit('logout');
      this.$router.push('/');
    }
  },
  computed: {
    showSidebar() {
      // 로그인 페이지에서는 상단바 숨김
      if (this.$route.path === '/' || this.$route.path === '/change-password') {
        return false;
      }
      return true;
    },
    // SOLUM 스타일 레이아웃 사용 회사 (대소문자 무관). SOLUM 및 SOLUM 기반 회사(FPT 등).
    // 이 회사들은 전용 대시보드/자산맵을 쓰고 일부 관리자 메뉴를 숨긴다.
    isSolum() {
      return this.$tenant.layout === 'solum';
    }
  }
};
</script>

<style scoped>
/* 전체 레이아웃 설정 */
.app-container {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background-color: #f5f5f5; /* 전체 배경을 연한 회색으로 */
  overflow: hidden; /* 전체 컨테이너에서 스크롤바 중복 방지 */
}

/* 상단 네비게이션 바 */
.top-navbar {
  background-color: #ffffff; /* 사이드바와 동일한 흰색 배경 */
  color: #333; /* 검은색 텍스트 */
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 2000; /* z-index를 1000에서 2000으로 증가 */
  box-shadow: none;              /* 그림자 제거 */
  border-bottom: none;           /* 하단 테두리 제거 */
}

/* 네비게이션 헤더 (로고 + 메뉴 버튼) */
.navbar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 10px; /* 상하 패딩을 10px로 증가 */
  height: 50px; /* 높이를 60px로 증가 */
}

/* 로고 영역 */
.navbar-logo {
  display: flex;
  align-items: center;
  gap: 12px; /* 로고와 텍스트 사이 간격 */
}

.top-company-logo {
  height: 44px; /* 로고 크기를 32px에서 44px로 증가 */
  width: auto;
}

/* SDAA 로고일 경우 파란색으로 변경 */
.sdaa-logo {
  filter: hue-rotate(240deg) saturate(200%) brightness(0.8);
}

/* 다른 회사 로고는 원본 색상 유지 */
.top-company-logo:not([src*="SDAA.png"]) {
  filter: none;
}

.company-name {
  font-size: 20px;
  font-weight: 600;
  color: #333;
  letter-spacing: 0.5px;
  text-transform: uppercase; /* 모든 글자를 대문자로 변환 */
}

/* 네비게이션 액션 영역 */
.navbar-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

/* 홈 아이콘 */
.home-icon {
  position: relative;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  transition: background-color 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.home-icon:hover {
  background-color: #f8f9fa;
}

.home-icon .material-symbols-outlined {
  font-size: 24px;
  color: #666;
  transition: color 0.3s;
}

.home-icon:hover .material-symbols-outlined {
  color: #333;
}

/* 알림 종 아이콘 */
.notification-bell {
  position: relative;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  transition: background-color 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.notification-bell:hover {
  background-color: #f8f9fa;
}

.bell-icon {
  font-size: 24px;
  color: #666;
  transition: color 0.3s;
}

.notification-bell:hover .bell-icon {
  color: #333;
}

.notification-badge {
  position: absolute;
  top: -2px;
  right: -2px;
  background-color: #dc3545;
  color: white;
  font-size: 12px;
  font-weight: 600;
  min-width: 18px;
  height: 18px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
  border: 2px solid white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

/* 메뉴 버튼 */
.navbar-menu-button {
  background: none;
  border: none;
  color: #333; /* 검은색 텍스트 */
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px; /* 간격을 8px에서 6px로 줄임 */
  padding: 6px 12px; /* 패딩을 8px 16px에서 6px 12px로 줄임 */
  border-radius: 6px;
  transition: background-color 0.3s;
  border: 1px solid #e0e0e0; /* 연한 테두리 추가 */
}

.navbar-menu-button:hover {
  background-color: #f8f9fa; /* 연한 회색 호버 */
}

.hamburger-icon {
  font-size: 16px; /* 아이콘 크기를 18px에서 16px로 줄임 */
}

.menu-text {
  font-size: 13px; /* 텍스트 크기를 14px에서 13px로 줄임 */
  font-weight: 500;
}

/* 드롭다운 메뉴 */
.dropdown-menu {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s ease-out;
  background-color: #ffffff; /* 흰색 배경 */
  border-top: 1px solid #e0e0e0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1); /* 그림자 추가 */
  z-index: 2001; /* 드롭다운 메뉴도 높은 z-index 설정 */
}

.dropdown-menu.menu-open {
  /* 가용 화면 높이 거의 전부 사용 + 넘치면 내부 스크롤.
     기존 360px 고정값은 admin 메뉴(11+ 항목) 가 잘리는 원인이었음. */
  max-height: calc(100vh - 50px);
  overflow-y: auto;
  transition: max-height 0.3s ease-in;
}

/* 메뉴 그리드 레이아웃 */
.menu-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px; /* 간격을 20px에서 16px로 줄임 */
  padding: 16px; /* 패딩을 20px에서 16px로 줄임 */
}

/* 메뉴 섹션 */
.menu-section h4 {
  color: #333; /* 검은색 텍스트 */
  margin: 0 0 12px 0; /* 하단 마진을 15px에서 12px로 줄임 */
  font-size: 15px; /* 폰트 크기를 16px에서 15px로 줄임 */
  border-bottom: 1px solid #e0e0e0; /* 연한 회색 테두리 */
  padding-bottom: 6px; /* 패딩을 8px에서 6px로 줄임 */
  font-weight: 600;
}

/* 메뉴 링크 */
.menu-section a {
  display: block;
  color: #666; /* 회색 텍스트 */
  text-decoration: none;
  padding: 6px 10px; /* 패딩을 8px 12px에서 6px 10px로 줄임 */
  margin: 2px 0;
  border-radius: 6px;
  transition: all 0.2s;
  font-size: 13px; /* 폰트 크기를 14px에서 13px로 줄임 */
}

.menu-section a:hover {
  background-color: #f8f9fa; /* 연한 회색 호버 */
  color: #333; /* 어두운 회색 텍스트 */
}

.menu-section a.router-link-active {
  background-color: #007bff;
  color: white;
}

/* 메인 컨텐츠 영역 */
.content-container {
  flex-grow: 1;
  margin-top: 60px;
  padding-top: 0px; /* 상단 여백 제거해서 헤더와 사이드가 바로 붙도록 */
  overflow-y: auto;
  background-color: #f5f5f5;
  min-height: calc(100vh - 60px);
  /* 스크롤바 스타일링으로 중복 방지 */
  scrollbar-width: thin;
  scrollbar-color: #c1c1c1 transparent;
}

/* 웹킷 브라우저용 스크롤바 스타일 */
.content-container::-webkit-scrollbar {
  width: 6px; /* 스크롤바 폭을 더 얇게 */
}

.content-container::-webkit-scrollbar-track {
  background: transparent; /* 트랙 배경 투명 */
}

.content-container::-webkit-scrollbar-thumb {
  background-color: rgba(193, 193, 193, 0.6); /* 반투명 스크롤바 */
  border-radius: 3px;
  border: none;
}

.content-container::-webkit-scrollbar-thumb:hover {
  background-color: rgba(161, 161, 161, 0.8); /* 호버 시 조금 더 진하게 */
}

.content-container::-webkit-scrollbar-corner {
  background: transparent; /* 모서리 투명 */
}

/* 반응형 디자인 */
@media screen and (max-width: 768px) {
  .menu-grid {
    grid-template-columns: 1fr;
  }
  
  .navbar-header {
    padding: 8px 15px; /* 모바일에서 패딩 조정 */
  }
  
  .navbar-logo {
    gap: 8px; /* 모바일에서 간격 축소 */
  }
  
  .navbar-actions {
    gap: 12px; /* 모바일에서 액션 간격 축소 */
  }
  
  .top-company-logo {
    height: 36px; /* 모바일에서 로고 크기를 44px에서 36px로 조정 */
  }
  
  .company-name {
    font-size: 16px; /* 모바일에서 텍스트 크기 축소 */
    font-weight: 600;
  }
  
  .bell-icon {
    font-size: 22px; /* 모바일에서 종 아이콘 크기 조정 */
  }
  
  .notification-badge {
    min-width: 16px;
    height: 16px;
    font-size: 11px;
  }
  
  .content-container {
    margin-top: 60px; /* 모바일에서도 동일한 높이 적용 */
  }
}

/* 언어 토글 (KO / EN) */
.lang-toggle {
  display: inline-flex;
  align-items: center;
  border: 1px solid #d0d7de;
  border-radius: 999px;
  overflow: hidden;
  margin-right: 8px;
  background: #ffffff;
}
.lang-btn {
  appearance: none;
  background: transparent;
  border: 0;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  color: #586069;
  cursor: pointer;
  line-height: 1.4;
  transition: background-color 0.15s ease, color 0.15s ease;
}
.lang-btn + .lang-btn { border-left: 1px solid #d0d7de; }
.lang-btn:hover { background: #f3f4f6; color: #24292f; }
.lang-btn.active {
  background: #1f4e79;
  color: #ffffff;
}
@media (max-width: 768px) {
  .lang-btn { padding: 3px 8px; font-size: 11px; }
  .lang-toggle { margin-right: 4px; }
}
</style>

<!-- 전역 스타일 추가 -->
<style>
html, body {
  overflow: hidden; /* html과 body의 스크롤바 제거 */
  height: 100%;
  margin: 0;
  padding: 0;
}

#app {
  height: 100vh;
  overflow: hidden; /* Vue 앱 루트에서도 스크롤바 제거 */
}
</style>
